import { Component, Inject, OnDestroy, OnInit, Renderer2, DOCUMENT } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { PermissionService } from '@services/permission.service';
import { FCMService } from '@services/fcm.service';
import { SwUpdate } from '@angular/service-worker';
import { AppUpdateService } from '@services/app-update.service';
import { AppDetailsDialogComponent } from '@shared/components/app-details-dialog/app-details-dialog.component';
import { NbWindowService } from '@nebular/theme';
import { environment } from '@env';
import * as moment from 'moment';
import { TranslateService } from '@ngx-translate/core';

import { Subscription } from 'rxjs';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'Maedah Admin';
  currentLanguage = 'en';
  subscription$ = new Subscription();
  constructor(
    private router: Router,
    private fcmService: FCMService,
    private permissionsService: PermissionService,
    private windowService: NbWindowService,
    private swUpdate: SwUpdate,
    private renderer: Renderer2,
    private translate: TranslateService,
    @Inject(DOCUMENT) private document: Document,
    private appUpdateService: AppUpdateService
  ) {
    this.permissionsService.loadPermissions();
    const localStorage = this.document.defaultView?.localStorage;
    if (localStorage) {
      const storedLang = localStorage.getItem('language');
      const defaultLang = storedLang || 'en';
      translate.setDefaultLang(defaultLang);
      translate.use(defaultLang);
      localStorage.setItem('language', defaultLang);
      this.currentLanguage = defaultLang;
      this.changeFontPerLang(defaultLang);
    }
    this.subscription$.add(
      this.translate.onLangChange.subscribe(event => {
        this.changeFontPerLang(event.lang);
      })
    );
  }

  async ngOnInit() {
    // In dev, clear stale SW registrations but keep Firebase messaging scope.
    await this.cleanupDevelopmentServiceWorkers();
    void this.fcmService.requestPermission();
    this.router.events.subscribe(evt => {
      if (evt instanceof NavigationEnd) {
        window.scrollTo(0, 0);
      }
    });

    const userprofile = JSON.parse(localStorage.getItem('user'));
    if (!userprofile) {
      this.router.navigate(['/auth/login']);
    }

    if (this.swUpdate.isEnabled) {
      this.appUpdateService.updateChecker().subscribe(() => {
        this.appUpdateService.availableStatus.next(true);
        this.windowService.open(AppDetailsDialogComponent, {
          title: 'Maedah Admin',
          context: { id: null },
          windowClass: 'custom-window',
          closeOnBackdropClick: false,
        });
      });
    }

    const style =
      'color:#2f6fbd; padding: 10px 20px; font-size: 20px; font-family: Courier New;';
    const rainbowStyle =
      'font-weight: bold; font-size: 50px; color: #c7931d; font-family: Courier New;';

    console.log('%cMaedah', rainbowStyle);
    console.log(
      `%cMaedah \nUpdated ${environment.updated_at} \nVersion ${environment.version}+${environment.build}`,
      style
    );
  }

  private changeFontPerLang(lang: string) {
    this.currentLanguage = lang;
    if (this.currentLanguage === 'ar') {
      this.document.body.style.fontFamily = `'Tajawal', sans-serif`;
      this.renderer.addClass(this.document.body, 'lang-ar');
      this.renderer.removeClass(this.document.body, 'lang-en');
    }
    if (this.currentLanguage === 'en') {
      this.document.body.style.fontFamily = `'DM-Sans', sans-serif`;
      this.renderer.addClass(this.document.body, 'lang-en');
      this.renderer.removeClass(this.document.body, 'lang-ar');
    }
  }

  ngOnDestroy(): void {
    this.subscription$.unsubscribe();
  }

  private async cleanupDevelopmentServiceWorkers(): Promise<void> {
    if (environment.production || !('serviceWorker' in navigator)) {
      return;
    }

    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(
      registrations.map(registration => {
        const scope = registration.scope || '';
        if (scope.includes('firebase-cloud-messaging-push-scope')) {
          return Promise.resolve(true);
        }

        return registration.unregister();
      })
    );
  }

  fromNow = (a) => {
    if (moment().diff(a, 'hours') > 24) {
      return `at ${a}`;
    }
    return moment(a).fromNow();
  };
}
